import { useCallback, useEffect } from "react";
import { _ROUTES } from "../../../utils/_routes_constants";
import { formDataToRow, excelStylesPlums } from "./utils";
import {
  _ACTIONS,
  _COLORS,
  _HTTP,
  _MESSAGES,
  _SWAL_ICONS,
} from "../../../utils/constants";
import { createToast } from "../../../utils/toast";
import { buildIRI } from "../../../utils/function";
import { DataTable } from "../../ui/DataTable";
import _ from "lodash";
import { DataService } from "../../../services/DataService";

const TablePlumsSplitSource = ({
  rows,
  columns,
  setRows,
  params,
  activityLRL,
}) => {
  const dataService = new DataService();

  useEffect(() => {
    if (params?.site !== undefined) {
      dataService.fetchData(
        _ROUTES.SITES + `/${params.site}`,
        { active: 1 },
        (data) => {
          const sources = data.sources;
          dataService.fetchData(
            _ROUTES.IPT_PLUMS_SPLIT_SOURCE,
            { ...params, "groups[]": "inputPlums:get","pagination":false },
            (data) => {
              const rowData = formDataToRow(data, sources, params);
              setRows(rowData);
            },
            "Unable to retrieve sources rows",
          );
        },
        "Unable to retrieve input plums split source rows",
      );
    }
  }, [params.site]);

  /**
   * Handles the event when a cell value is changed in the table.
   *
   * @param {Object} agParams - The parameters provided by ag-Grid when a cell value changes.
   * @param {Object} agParams.colDef - The column definition of the changed cell.
   * @param {Object} agParams.data - The data of the row containing the changed cell.
   * @param {string|number} agParams.newValue - The new value entered in the cell.
   * @param {Object} agParams.node - The row node containing the changed cell.
   *
   * @returns {Promise<void>} - A promise that resolves when the request is completed.
   */
  const handleCellValueChanged = useCallback(
    async (agParams) => {
      const { colDef, data, newValue, node } = agParams;
      const field = colDef.field;
      const month = parseInt(field.replace("months.", ""));

      const payload = {
        amount: parseFloat(newValue) || 0,
        sources: buildIRI("/sources", data.sourceId),
        fiscalMonth: month,
        fiscalYear: params.fiscalYear,
        version: params.version || 1,
        activity: buildIRI("/activities", activityLRL.id),
        site: buildIRI("/sites", params.site),
      };

      const inputPlumsId = data.inputPlumsId;
      if (!_.isUndefined(inputPlumsId[month])) {
        payload.id = inputPlumsId[month];
      }

      const method = payload.id ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url = payload.id
        ? `${_ROUTES.IPT_PLUMS_SPLIT_SOURCE}/${payload.id}`
        : _ROUTES.IPT_PLUMS_SPLIT_SOURCE;

      dataService
        .doRequest(method, url, payload)
        .then((res) => {
          if (res.status === _HTTP.created || res.status === _HTTP.ok) {
            createToast(
              _SWAL_ICONS.success,
              "Plum split source saved successfully",
            );
            if (method === _ACTIONS.POST && res.data?.id) {
              const updatedData = {
                ...data,
                inputPlumsId: {
                  ...data.inputPlumsId,
                  [month]: res.data.id,
                },
              };
              node.setData(updatedData);
            }
          }
        })
        .catch(() => {
          createToast(_SWAL_ICONS.error, _MESSAGES.error);
        });
    },
    [params],
  );

  return (
    <DataTable
      rows={rows}
      columns={columns}
      excelStyles={excelStylesPlums}
      onCellValueChanged={handleCellValueChanged}
      fullHeight={true}
      agGridProps={{
        cellSelection: true,
        tooltipShowDelay: 0,
        enableBrowserTooltips: true,
        grandTotalRow: "bottom",
        getRowStyle: (params) => {
          if (params.node.footer) {
            params.node.aggData.rowName = "Plums Split Source %";
            return { backgroundColor: _COLORS.groupRowColor };
          }

          return null;
        },
      }}
    />
  );
};

export default TablePlumsSplitSource;
